/**
 * Shared helpers for matching inventory location barcodes / typed text
 * (hardware wedge, camera scan, manual entry) across mobile devices.
 */

import { getLocationId, isDefaultLocation } from "./index";

export function safeLocationPart(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function parseScannedValue(raw) {
  if (typeof raw === "string") return raw;
  if (raw && typeof raw.data === "string") return raw.data;
  if (raw?.nativeEvent && typeof raw.nativeEvent.data === "string") return raw.nativeEvent.data;
  if (raw == null) return "";
  return String(raw);
}

/** Underlying DOM node for an RN Web TextInput ref (input or textarea). */
export function getTextInputDomNode(ref) {
  const el = ref && ref.current;
  if (!el) return null;
  const tag = (el.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA") return el;
  if (typeof el.querySelector === "function") {
    return el.querySelector("input, textarea");
  }
  return null;
}

/** Strip control chars (Zebra/DataWedge often suffix \\r\\n\\t). */
export function cleanScannedText(raw) {
  return parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
}

/**
 * Clean Android/Sincoole wedge noise on commit only — strips prefix garbage but
 * keeps the full hyphenated barcode (never truncates at the first segment).
 */
export function normalizeWedgeScanText(raw) {
  let text = parseScannedValue(raw);
  text = text.replace(/[\x00-\x1F\x7F]/g, "");
  text = text.replace(/^\\+(?:0)+/, "");
  text = text.trim();
  // Strip leading slashes/backslashes before stripping leading digits,
  // so "/000026site1/bin1" → "000026site1/bin1" → "site1/bin1".
  text = text.replace(/^[/\\]+/, "");
  text = text.replace(/^0+(?=[\sA-Za-z/])/, "");
  text = text.replace(/^[\d\\]+(?=[A-Za-z])/, "");
  text = text.trim();
  // Location labels like "Site 1A" or "Default Location" keep interior spaces.
  if (/\s/.test(text)) return text;
  const hyphenated = text.match(/[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+/);
  if (hyphenated) return hyphenated[0];
  if (text.includes("-")) {
    return text.replace(/-+$/, "");
  }
  // Allow "/" as a valid separator inside a barcode (e.g. "site1/bin1").
  const plain = text.match(/[A-Za-z0-9][A-Za-z0-9/]*/);
  if (plain) return plain[0];
  return text.trim();
}

/** Fix double-capture when beforeinput sync + native insert both run (ssiittee11 → site1). */
export function dedupeDoubledWedgeScan(text) {
  const t = cleanScannedText(text);
  if (!t || t.length < 4) return t;

  let collapsed = "";
  let pairedChars = 0;
  let i = 0;
  while (i < t.length) {
    const ch = t[i];
    if (i + 1 < t.length && t[i + 1] === ch && /[A-Za-z0-9]/.test(ch)) {
      collapsed += ch;
      pairedChars += 2;
      i += 2;
    } else {
      collapsed += ch;
      i += 1;
    }
  }

  if (collapsed === t) return t;
  if (pairedChars < t.length * 0.75) return t;
  if (!/[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)+/.test(collapsed)) return t;
  return collapsed;
}

/** True while only the Sincoole prefix has arrived (hide from the field). */
export function isIncompleteWedgePrefix(text) {
  const t = cleanScannedText(text);
  if (!t) return false;
  return (
    /^[/\\]*0*$/.test(t) ||
    /^0{1,6}$/.test(t) ||
    /^[/\\]0{1,6}$/.test(t) ||
    /^[/\\]000026$/.test(t)
  );
}

/** Strip control chars (Zebra/DataWedge often suffix \\r\\n) and normalize separators for comparison. */
export function normalizeScannedLocation(data) {
  var scanned = parseScannedValue(data)
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim();
  if (!scanned) return "";

  var scannedArray = scanned.split("-");
  if (scannedArray.length <= 1) {
    scannedArray = scanned.split("/");
  }
  if (scannedArray.length > 1) {
    scanned = "";
    scannedArray.forEach((part) => {
      scanned += part.trim().replace(/ /g, "");
    });
  } else {
    scanned = scanned.replace(/ /g, "");
  }
  return scanned.toLowerCase();
}

export function renderLocationLabel(location) {
  if (location == null || location === "") return "";
  if (typeof location === "string") return location;
  var item = location;
  if (typeof item === "object" && item?.length > 0) {
    item = item[0];
    if (typeof item === "string") return item;
  }
  if (!item || typeof item !== "object") return "";

  var completeLocation = "";
  if (item?.site?.locationName) completeLocation += safeLocationPart(item.site.locationName);
  if (item?.zone?.locationName) completeLocation += "-" + safeLocationPart(item.zone.locationName);
  if (item?.aisle?.locationName) completeLocation += "-" + safeLocationPart(item.aisle.locationName);
  if (item?.rackBay?.locationName) completeLocation += "-" + safeLocationPart(item.rackBay.locationName);
  if (item?.shelfLevel?.locationName) completeLocation += "-" + safeLocationPart(item.shelfLevel.locationName);
  if (item?.bin?.locationName) completeLocation += "-" + safeLocationPart(item.bin.locationName);
  return completeLocation;
}

/** Concatenated path without separators (legacy scan format). */
export function getLocationMatchKey(location) {
  var completeLocation = "";
  if (location?.site?.locationName) completeLocation += safeLocationPart(location.site.locationName);
  if (location?.zone?.locationName) completeLocation += safeLocationPart(location.zone.locationName);
  if (location?.aisle?.locationName) completeLocation += safeLocationPart(location.aisle.locationName);
  if (location?.rackBay?.locationName) completeLocation += safeLocationPart(location.rackBay.locationName);
  if (location?.shelfLevel?.locationName) completeLocation += safeLocationPart(location.shelfLevel.locationName);
  if (location?.bin?.locationName) completeLocation += safeLocationPart(location.bin.locationName);
  return completeLocation.trim().replace(/ /g, "").replace(/[/-]+/g, "").toLowerCase();
}

function uniqueByLocationId(candidates) {
  var seen = {};
  return candidates.filter((loc) => {
    var key = getLocationMatchKey(loc);
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

/** Case-insensitive label compare; keeps hyphen/slash path structure. */
export function normalizeLocationCompareText(raw) {
  return String(parseScannedValue(raw) || "")
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[\\/]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function locationSpecificityScore(loc) {
  var label = normalizeLocationCompareText(renderLocationLabel(loc));
  var key = getLocationMatchKey(loc);
  return Math.max(label.length, key.length);
}

function pickMostSpecificLocation(candidates) {
  var unique = uniqueByLocationId(candidates);
  if (!unique.length) return null;
  if (unique.length === 1) return unique[0];
  unique.sort(function (a, b) {
    return locationSpecificityScore(b) - locationSpecificityScore(a);
  });
  return unique[0];
}

function scannedHasPathSeparator(scannedRaw) {
  return /[-/]/.test(
    String(parseScannedValue(scannedRaw) || "").replace(/[\x00-\x1F\x7F]/g, "")
  );
}

function scannedPathMatchesLabel(compareText, label) {
  if (!compareText || !label) return false;
  if (label === compareText) return true;
  if (label.endsWith("-" + compareText)) return true;
  var scanParts = compareText.split("-");
  var labelParts = label.split("-");
  if (labelParts.length >= scanParts.length) {
    if (labelParts.slice(-scanParts.length).join("-") === compareText) return true;
  }
  if (scanParts.length === 2 && labelParts.length >= 2) {
    return labelParts[0] === scanParts[0] && labelParts[labelParts.length - 1] === scanParts[1];
  }
  return false;
}

/**
 * Resolve a scan or typed string to one inventory location object, or null if ambiguous / not found.
 */
export function findLocationInList(scannedRaw, locations, { exactOnly = false } = {}) {
  if (!Array.isArray(locations) || !locations.length) return null;

  var compareText = normalizeLocationCompareText(scannedRaw);
  var norm = normalizeScannedLocation(scannedRaw);
  if (!compareText && !norm) return null;

  var hasPathSeparator = scannedHasPathSeparator(scannedRaw);

  // Exact hyphenated label (site1-bin1), case-insensitive.
  var exactLabelCompare = locations.filter(function (loc) {
    return normalizeLocationCompareText(renderLocationLabel(loc)) === compareText;
  });
  var exactLabelPick = pickMostSpecificLocation(exactLabelCompare);
  if (exactLabelPick) return exactLabelPick;

  // Exact concatenated key (legacy format without separators).
  var exactKey = locations.filter(function (loc) {
    return getLocationMatchKey(loc) === norm;
  });
  var exactKeyPick = pickMostSpecificLocation(exactKey);
  if (exactKeyPick) return exactKeyPick;

  var exactLabelNorm = locations.filter(function (loc) {
    return normalizeScannedLocation(renderLocationLabel(loc)) === norm;
  });
  var exactLabelNormPick = pickMostSpecificLocation(exactLabelNorm);
  if (exactLabelNormPick) return exactLabelNormPick;

  // Hyphenated scan: match full path suffix (site1-bin1, site1-zone-bin1).
  if (hasPathSeparator) {
    var pathMatches = locations.filter(function (loc) {
      return scannedPathMatchesLabel(
        compareText,
        normalizeLocationCompareText(renderLocationLabel(loc))
      );
    });
    var pathPick = pickMostSpecificLocation(pathMatches);
    if (pathPick) return pathPick;
  }

  if (exactOnly) return null;

  var suffixMatches = locations.filter(function (loc) {
    var key = getLocationMatchKey(loc);
    return key && (key.endsWith(norm) || norm.endsWith(key));
  });
  var suffixPick = pickMostSpecificLocation(suffixMatches);
  if (suffixPick) return suffixPick;

  var includesMatches = locations.filter(function (loc) {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      (key && (key.includes(norm) || norm.includes(key))) ||
      (labelNorm && (labelNorm.includes(norm) || norm.includes(labelNorm)))
    );
  });
  var includesPick = pickMostSpecificLocation(includesMatches);
  if (includesPick) {
    var pickedKey = getLocationMatchKey(includesPick);
    // Do not match a shorter parent when the scan is a longer path (site1-bin1 ≠ site1).
    if (norm.length > pickedKey.length && !norm.endsWith(pickedKey)) {
      return null;
    }
    return includesPick;
  }

  return null;
}

function resolveLocationValueFromList(value, locations, { exactOnly = true } = {}) {
  if (value == null || value === "") return value;

  var objectValue = null;
  var text = value;
  if (typeof value === "object" && !Array.isArray(value)) {
    objectValue = value;
  } else if (Array.isArray(value) && value.length > 0) {
    if (typeof value[0] === "object" && value[0] != null) objectValue = value[0];
    else if (typeof value[0] === "string") text = value[0];
    else return value;
  }

  if (!Array.isArray(locations) || !locations.length) {
    return objectValue || value;
  }

  if (objectValue) {
    var label = renderLocationLabel(objectValue);
    if (label) {
      var fromList = findLocationInList(label, locations, { exactOnly });
      if (!fromList && exactOnly) {
        fromList = findLocationInList(label, locations, { exactOnly: false });
      }
      if (fromList) return fromList;
    }
    return objectValue;
  }

  if (typeof text === "string" && text.trim()) {
    var match = findLocationInList(text, locations, { exactOnly });
    if (!match && exactOnly) {
      match = findLocationInList(text, locations, { exactOnly: false });
    }
    return match || text;
  }
  return value;
}

/** Turn a submission location value (object or scanned label string) into a location object. */
export function resolveSubmissionLocationValue(value, locations, { exactOnly = true } = {}) {
  return resolveLocationValueFromList(value, locations, { exactOnly });
}

/** Aggressive resolve for inventory fetch — always prefer the most specific location match. */
export function resolveLocationForItemsFetch(value, locations) {
  if (value == null || value === "") return null;

  var objectValue = null;
  var text = value;
  if (typeof value === "object" && !Array.isArray(value)) {
    objectValue = value;
  } else if (Array.isArray(value) && value.length > 0) {
    if (typeof value[0] === "object" && value[0] != null) objectValue = value[0];
    else if (typeof value[0] === "string") text = value[0];
  }

  if (!Array.isArray(locations) || !locations.length) {
    return objectValue || (typeof text === "object" ? text : null);
  }

  if (objectValue) {
    var label = renderLocationLabel(objectValue);
    if (label) {
      var fromList =
        findLocationInList(label, locations, { exactOnly: true }) ||
        findLocationInList(label, locations, { exactOnly: false });
      if (fromList) return fromList;
    }
    return objectValue;
  }

  if (typeof text !== "string" || !text.trim()) return null;
  return (
    findLocationInList(text, locations, { exactOnly: true }) ||
    findLocationInList(text, locations, { exactOnly: false })
  );
}

/** Resolve location and build POST body for getItemsByLocation (deepest level _id). */
export function buildGetItemsByLocationPayload(location, inventoryLocations) {
  var resolved = resolveLocationForItemsFetch(location, inventoryLocations);
  if (!resolved || typeof resolved !== "object") return null;

  var id = getLocationId(resolved);
  if (!id) return null;

  var data = { id };
  if (isDefaultLocation(resolved)) data.defaultLocation = true;
  return { data, location: resolved };
}

export function locationListIncludesTerm(locations, term) {
  var norm = normalizeScannedLocation(term);
  if (!norm) return false;
  return locations.some((loc) => {
    var key = getLocationMatchKey(loc);
    var labelNorm = normalizeScannedLocation(renderLocationLabel(loc));
    return (
      key === norm ||
      labelNorm === norm ||
      key.includes(norm) ||
      labelNorm.includes(norm)
    );
  });
}

/* ---- Android Chrome + Symbol wedge: buffer keystrokes, block Enter ---- */

const CAPTURE_OPTS = { capture: true, passive: false };
const POST_SCAN_ENTER_MS = 2500;
const IDLE_FLUSH_MS = 900;
const MIN_SCAN_LENGTH = 2;
/** Wait until barcode text stops growing before committing (Zebra drops prefix on first scan). */
const SETTLE_STABLE_MS = 120;
const SETTLE_MAX_WAIT_MS = 1500;
const SETTLE_POLL_MS = 40;

let wedgeSessionActive = false;
let scanCallback = null;
let progressCallback = null;
let keyBuffer = "";
let idleFlushTimer = null;
let enterShieldActive = false;
let blockEnterUntil = 0;
let wedgeTeardown = null;
let lastKeyAt = 0;
let captureInput = null;
let activeWedgeOwnerId = null;
let fallbackScanGetter = null;
let globalEnterTrapCount = 0;
let globalEnterTrapTeardown = null;
let wedgeUsesHiddenCapture = false;
let _trapLastFocused = null;
let _trapFocusRecoveryTimer = null;

function isTextInputElement(el) {
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName.toUpperCase();
  if (tag !== "INPUT" && tag !== "TEXTAREA") return false;
  if (el.readOnly || el.disabled) return false;
  if (tag === "INPUT") {
    const type = (el.type || "text").toLowerCase();
    if (type === "hidden" || type === "submit" || type === "button" || type === "checkbox" || type === "radio") {
      return false;
    }
  }
  return true;
}

function isWedgeRelatedTarget(target) {
  if (!target || typeof target.getAttribute !== "function") return false;
  if (target.getAttribute("data-wedge-scan") === "true") return true;
  if (target.getAttribute("data-wedge-capture") === "true") return true;
  if (typeof target.closest === "function") {
    return !!target.closest("[data-wedge-scan],[data-wedge-capture]");
  }
  return false;
}

function isNavigationKey(e) {
  const code = e.keyCode || e.which;
  const key = e?.key;
  return (
    key === "Search" ||
    key === "F3" ||
    code === 114 ||
    code === 122
  );
}

function usesVisibleFieldWedgeMode() {
  return wedgeSessionActive && typeof fallbackScanGetter === "function";
}

function shouldTrapChromeEnter(e) {
  if (Date.now() < scanGuardBlockEnterUntil) return true;
  if (wedgeSessionActive || enterShieldActive || Date.now() < blockEnterUntil) {
    return true;
  }
  if (isEnterKey(e) || isTabKey(e) || isNavigationKey(e)) {
    const target = e?.target;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (isWedgeRelatedTarget(target) || isWedgeRelatedTarget(active)) {
      return true;
    }
  }
  return false;
}

function onGlobalEnterTrap(e) {
  if (!globalEnterTrapCount || !shouldTrapChromeEnter(e)) return;
  if (!isEnterKey(e) && !isTabKey(e) && !isNavigationKey(e)) return;

  const active = typeof document !== "undefined" ? document.activeElement : null;
  const wedgeField =
    isWedgeRelatedTarget(e?.target) || isWedgeRelatedTarget(active);

  e.preventDefault();

  if (wedgeSessionActive) {
    if (scanCallback) {
      swallowEnter(e);
      completeBufferedScan({
        defer: usesVisibleFieldWedgeMode() || wedgeUsesHiddenCapture,
      });
    }
    return;
  }

  if (wedgeField) {
    // Block Chrome navigation; field DOM handlers commit the scan.
    return;
  }

  swallowEnter(e);

  if (active && isWedgeRelatedTarget(active) && scanCallback) {
    const val = cleanScannedText(active.value || "");
    if (val && val.length >= MIN_SCAN_LENGTH && !isGarbledKeyBuffer(val)) {
      blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
      try {
        scanCallback(val);
      } catch (err) {
        console.warn("symbolWedgeSession first-scan callback:", err);
      }
    }
  } else if (wedgeSessionActive && scanCallback && typeof fallbackScanGetter !== "function") {
    completeBufferedScan();
  }
}

function onGlobalEnterTrapLate(e) {
  if (!globalEnterTrapCount || !shouldTrapChromeEnter(e)) return;
  if (isEnterKey(e) || isTabKey(e)) {
    e.preventDefault();
  }
}

function onGlobalBeforeInputTrap(e) {
  if (!globalEnterTrapCount) return;
  if (!shouldTrapChromeEnter(e)) return;
  if (e.inputType === "insertLineBreak" || e.data === "\n" || e.data === "\r") {
    e.preventDefault();
    const active = typeof document !== "undefined" ? document.activeElement : null;
    const handlerEl =
      (active && active._gohaccpWedgeEnterHandler && active) ||
      (_trapLastFocused && _trapLastFocused._gohaccpWedgeEnterHandler && _trapLastFocused) ||
      null;
    if (handlerEl) {
      try {
        handlerEl._gohaccpWedgeEnterHandler();
      } catch (_) {}
    }
  }
}

function onPreScanKeydown(e) {
  if (!globalEnterTrapCount) return;
  if (isEnterKey(e) || isTabKey(e)) return;
  const ch = keyEventToChar(e);
  if (!ch || e.ctrlKey || e.metaKey || e.altKey) return;
  const target = e?.target;
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (isTextInputElement(target) || isTextInputElement(active) || isWedgeRelatedTarget(target)) {
    blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
  }
}

function onTrapScanInput(e) {
  if (!globalEnterTrapCount) return;
  const target = e?.target;
  if (!isTextInputElement(target) && !isWedgeRelatedTarget(target)) return;
  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
  if (isTextInputElement(target)) _trapLastFocused = target;
}

function onTrapPointerDown(e) {
  if (!globalEnterTrapCount) return;
  const target = e?.target;
  if (isTextInputElement(target) || isWedgeRelatedTarget(target)) {
    blockEnterUntil = Date.now() + 15000;
    if (isTextInputElement(target)) _trapLastFocused = target;
  }
}

function onTrapFocusIn(e) {
  if (!globalEnterTrapCount) return;
  const target = e?.target;
  if (isTextInputElement(target) || isWedgeRelatedTarget(target)) {
    blockEnterUntil = Date.now() + 15000;
    if (isTextInputElement(target)) _trapLastFocused = target;
  }
}

function attachGlobalEnterTrap() {
  if (globalEnterTrapTeardown || typeof window === "undefined") return;
  window.addEventListener("keydown", onPreScanKeydown, CAPTURE_OPTS);
  window.addEventListener("keydown", onGlobalEnterTrap, CAPTURE_OPTS);
  window.addEventListener("keypress", onGlobalEnterTrapLate, CAPTURE_OPTS);
  window.addEventListener("keyup", onGlobalEnterTrapLate, CAPTURE_OPTS);
  window.addEventListener("beforeinput", onGlobalBeforeInputTrap, CAPTURE_OPTS);
  window.addEventListener("input", onTrapScanInput, CAPTURE_OPTS);
  if (typeof document !== "undefined") {
    document.addEventListener("pointerdown", onTrapPointerDown, CAPTURE_OPTS);
    document.addEventListener("touchstart", onTrapPointerDown, CAPTURE_OPTS);
    document.addEventListener("focusin", onTrapFocusIn, CAPTURE_OPTS);
  }
  globalEnterTrapTeardown = () => {
    window.removeEventListener("keydown", onPreScanKeydown, CAPTURE_OPTS);
    window.removeEventListener("keydown", onGlobalEnterTrap, CAPTURE_OPTS);
    window.removeEventListener("keypress", onGlobalEnterTrapLate, CAPTURE_OPTS);
    window.removeEventListener("keyup", onGlobalEnterTrapLate, CAPTURE_OPTS);
    window.removeEventListener("beforeinput", onGlobalBeforeInputTrap, CAPTURE_OPTS);
    window.removeEventListener("input", onTrapScanInput, CAPTURE_OPTS);
    if (typeof document !== "undefined") {
      document.removeEventListener("pointerdown", onTrapPointerDown, CAPTURE_OPTS);
      document.removeEventListener("touchstart", onTrapPointerDown, CAPTURE_OPTS);
      document.removeEventListener("focusin", onTrapFocusIn, CAPTURE_OPTS);
    }
    _trapLastFocused = null;
    if (_trapFocusRecoveryTimer) {
      clearTimeout(_trapFocusRecoveryTimer);
      _trapFocusRecoveryTimer = null;
    }
  };
}

/** Ref-counted Enter trap for Shipment-by-Location on Android Chrome (survives child remounts). */
export function installGlobalChromeEnterTrap() {
  globalEnterTrapCount += 1;
  attachGlobalEnterTrap();
}

export function releaseGlobalChromeEnterTrap() {
  globalEnterTrapCount = Math.max(0, globalEnterTrapCount - 1);
  if (globalEnterTrapCount === 0 && globalEnterTrapTeardown) {
    globalEnterTrapTeardown();
    globalEnterTrapTeardown = null;
  }
}

export function getActiveWedgeOwnerId() {
  return activeWedgeOwnerId;
}

/** Only one location row owns the wedge at a time (prevents focus fights in tables). */
let activeLocationFieldOwnerId = null;
let activeLocationFieldRelease = null;

export function registerActiveLocationWedgeField(ownerId, releaseFn) {
  if (activeLocationFieldRelease && activeLocationFieldOwnerId !== ownerId) {
    try {
      activeLocationFieldRelease();
    } catch (_) {}
  }
  activeLocationFieldOwnerId = ownerId || null;
  activeLocationFieldRelease = releaseFn || null;
}

export function unregisterActiveLocationWedgeField(ownerId) {
  if (activeLocationFieldOwnerId === ownerId) {
    activeLocationFieldOwnerId = null;
    activeLocationFieldRelease = null;
  }
}

export function getActiveLocationWedgeOwnerId() {
  return activeLocationFieldOwnerId;
}

function containsTerminator(text) {
  return /[\r\n\t]/.test(text || "");
}

function ensureCaptureInput() {
  if (typeof document === "undefined") return null;
  if (captureInput) return captureInput;

  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "text";
  input.enterKeyHint = "done";
  input.setAttribute("data-wedge-capture", "true");
  input.setAttribute("aria-hidden", "true");
  input.setAttribute("autocomplete", "off");
  input.setAttribute("autocorrect", "off");
  input.setAttribute("autocapitalize", "off");
  input.setAttribute("spellcheck", "false");
  Object.assign(input.style, {
    position: "fixed",
    // Keep a real focusable target — 0-size / pointer-events:none often rejects
    // hardware wedge input on Android Chrome until a second scan.
    bottom: "0",
    left: "0",
    width: "100%",
    height: "44px",
    opacity: "0.01",
    padding: "0",
    margin: "0",
    border: "none",
    outline: "none",
    pointerEvents: "none",
    zIndex: "-1",
    fontSize: "16px",
    background: "transparent",
    color: "transparent",
    caretColor: "transparent",
  });

  input.addEventListener("input", () => {
    if (!wedgeSessionActive) return;
    const val = input.value || "";
    lastKeyAt = Date.now();
    if (containsTerminator(val)) {
      keyBuffer = val;
      completeBufferedScan({ defer: true });
      return;
    }
    if (val.length >= keyBuffer.length || !keyBuffer) {
      keyBuffer = val;
      if (progressCallback && keyBuffer.length >= 1) {
        try {
          progressCallback(cleanScannedText(keyBuffer));
        } catch (e) {
          console.warn("symbolWedgeSession progress callback:", e);
        }
      }
      scheduleIdleFlush();
    }
  });

  document.body.appendChild(input);
  captureInput = input;
  return input;
}

export function isSymbolWedgeSessionActive() {
  return wedgeSessionActive;
}

export function focusSymbolWedgeCapture() {
  if (!wedgeSessionActive) return;
  const node = ensureCaptureInput();
  if (!node) return;
  try {
    // Clear stale value from a previous failed first-scan attempt.
    if (document.activeElement !== node && !(node.value || "").length) {
      // keep empty
    }
    node.focus({ preventScroll: true });
  } catch (e) {
    node.focus();
  }
}

/** Create the capture input early (form mount) so the first laser scan has a target. */
export function ensureSymbolWedgeCaptureReady() {
  return ensureCaptureInput();
}

export function isSymbolWedgeCaptureFocused() {
  return typeof document !== "undefined" && document.activeElement === captureInput;
}

function clearCaptureInputValue() {
  if (captureInput) captureInput.value = "";
}

function isEnterKey(e) {
  const key = e?.key;
  const code = e?.keyCode || e?.which;
  return key === "Enter" || key === "Return" || code === 13;
}

function isTabKey(e) {
  return e?.key === "Tab" || e?.keyCode === 9;
}

function swallowEnter(e) {
  e.preventDefault();
  if (typeof e.stopImmediatePropagation === "function") {
    e.stopImmediatePropagation();
  }
  if (typeof e.stopPropagation === "function") {
    e.stopPropagation();
  }
}

function shouldBlockEnterNow() {
  return wedgeSessionActive || enterShieldActive || Date.now() < blockEnterUntil;
}

function keyEventToChar(e) {
  const key = e.key;
  if (key && key.length === 1 && key !== "Unidentified") return key;
  const code = e.keyCode || e.which;
  if (!code) return "";
  if (code >= 48 && code <= 57) return String.fromCharCode(code);
  if (code >= 65 && code <= 90) return String.fromCharCode(code);
  if (code >= 97 && code <= 122) return String.fromCharCode(code);
  if (code >= 32 && code <= 126) return String.fromCharCode(code);
  return "";
}

function clearIdleFlush() {
  if (idleFlushTimer) {
    clearTimeout(idleFlushTimer);
    idleFlushTimer = null;
  }
}

function scheduleIdleFlush() {
  clearIdleFlush();
  idleFlushTimer = setTimeout(() => {
    idleFlushTimer = null;
    completeBufferedScan();
  }, IDLE_FLUSH_MS);
}

function isGarbledKeyBuffer(text) {
  const clean = cleanScannedText(text);
  return clean.length >= 2 && /^(.)\1*$/.test(clean);
}

/** Android Chrome wedge keydown buffers are often repeated garbage (uuuuu, 0000). */
export function isGarbledWedgeScanText(text) {
  return isGarbledKeyBuffer(text);
}

function mergeScanFragments(a, b) {
  if (!a) return b || "";
  if (!b) return a;
  if (a === b) return a;
  if (a.includes(b)) return a;
  if (b.includes(a)) return b;

  // Overlap merge: "site1-" + "e1-bin1" → "site1-bin1"
  const maxOverlap = Math.min(a.length, b.length);
  for (let i = maxOverlap; i > 0; i--) {
    if (a.endsWith(b.slice(0, i))) {
      return a + b.slice(i);
    }
    if (b.endsWith(a.slice(0, i))) {
      return b + a.slice(i);
    }
  }

  const looksLikeBarcode = (s) =>
    /[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+/.test(s) || /[A-Za-z0-9]+(?:\/[A-Za-z0-9]+)+/.test(s);

  // First-scan split: short prefix on visible field ("sit"), rest on capture ("e1-bin1").
  // Prefer short-prefix + long-suffix when only one side has a separator.
  const aHasSep = /[-/]/.test(a);
  const bHasSep = /[-/]/.test(b);
  if (!aHasSep && bHasSep && looksLikeBarcode(a + b)) return a + b;
  if (!bHasSep && aHasSep && looksLikeBarcode(b + a)) return b + a;

  const ab = a + b;
  const ba = b + a;
  if (looksLikeBarcode(ab) && !looksLikeBarcode(ba)) return ab;
  if (looksLikeBarcode(ba) && !looksLikeBarcode(ab)) return ba;
  return a.length >= b.length ? a : b;
}

export function readPassiveWedgeCaptureValue() {
  if (!captureInput || typeof captureInput.value !== "string") return "";
  return cleanScannedText(captureInput.value);
}

export function clearPassiveWedgeCaptureValue() {
  clearCaptureInputValue();
}

export function mergeWedgeScanParts(a, b) {
  return mergeScanFragments(a || "", b || "");
}

function readPendingScan() {
  let fromFallback = "";
  if (typeof fallbackScanGetter === "function") {
    try {
      fromFallback = cleanScannedText(fallbackScanGetter());
    } catch (e) {}
  }
  const fromCapture = cleanScannedText(captureInput?.value || "");
  const fromBuffer = cleanScannedText(keyBuffer);

  const candidates = [];
  if (fromCapture && !isGarbledKeyBuffer(fromCapture) && !isSincooleGarbage(fromCapture)) {
    candidates.push(fromCapture);
  }
  if (fromFallback && !isGarbledKeyBuffer(fromFallback) && !isSincooleGarbage(fromFallback)) {
    candidates.push(fromFallback);
  }
  // Only use keydown buffer when it looks like a real barcode (has separator).
  if (
    fromBuffer &&
    !isGarbledKeyBuffer(fromBuffer) &&
    !isSincooleGarbage(fromBuffer) &&
    /[-/]/.test(fromBuffer)
  ) {
    candidates.push(fromBuffer);
  }
  if (!candidates.length) return "";

  let best = candidates[0];
  for (let i = 1; i < candidates.length; i++) {
    best = mergeScanFragments(best, candidates[i]);
  }
  return best;
}

/** Zebra keydown often yields Sincoole prefix + truncated letters (e.g. "000026sie"). */
function isSincooleGarbage(text) {
  const clean = cleanScannedText(text);
  if (!clean) return false;
  // Leading zeros + short alpha fragment, no real location separator.
  if (/^0{2,}\d*[A-Za-z]{1,4}$/.test(clean) && !/[-/]/.test(clean)) return true;
  // After digit-strip would be a tiny fragment like "sie".
  const stripped = clean.replace(/^[\d\\/]+(?=[A-Za-z])/, "");
  if (stripped.length > 0 && stripped.length <= 4 && !/[-/]/.test(stripped) && /^0{2,}/.test(clean)) {
    return true;
  }
  return false;
}

function commitBufferedScanValue(raw) {
  if (!raw || raw.length < MIN_SCAN_LENGTH || !scanCallback) {
    if (wedgeSessionActive) {
      scheduleIdleFlush();
    }
    return false;
  }
  if (isGarbledKeyBuffer(raw)) {
    scheduleIdleFlush();
    return false;
  }

  keyBuffer = "";
  clearCaptureInputValue();
  clearIdleFlush();
  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;
  try {
    scanCallback(raw);
  } catch (e) {
    console.warn("symbolWedgeSession scan callback:", e);
  } finally {
    setTimeout(() => {
      if (!wedgeSessionActive || !wedgeUsesHiddenCapture) return;
      focusSymbolWedgeCapture();
    }, 50);
  }
  return true;
}

/**
 * Wait until the pending barcode stops growing, then commit.
 * Fixes Zebra first-scan truncation (e.g. site1-bin1 → e1-bin1).
 */
function completeSettledBufferedScan() {
  const startedAt = Date.now();
  let lastRaw = "";
  let lastChangeAt = Date.now();

  const tryCommit = () => {
    if (!wedgeSessionActive) return;
    const raw = readPendingScan();
    const now = Date.now();

    if (raw && raw.length > lastRaw.length) {
      lastRaw = raw;
      lastChangeAt = now;
    }

    const stable = lastRaw && now - lastChangeAt >= SETTLE_STABLE_MS;
    const timedOut = now - startedAt >= SETTLE_MAX_WAIT_MS;

    if ((stable || timedOut) && lastRaw) {
      commitBufferedScanValue(lastRaw);
      return;
    }

    if (timedOut) {
      scheduleIdleFlush();
      return;
    }

    setTimeout(tryCommit, SETTLE_POLL_MS);
  };

  tryCommit();
}

function completeBufferedScan({ defer = false } = {}) {
  if (defer || usesVisibleFieldWedgeMode() || wedgeUsesHiddenCapture) {
    completeSettledBufferedScan();
    return;
  }
  commitBufferedScanValue(readPendingScan());
}

function onWedgeKeyDown(e) {
  if (wedgeSessionActive && isNavigationKey(e)) {
    swallowEnter(e);
    return;
  }

  if (isEnterKey(e) || isTabKey(e)) {
    if (wedgeSessionActive) {
      e.preventDefault();
      const active = typeof document !== "undefined" ? document.activeElement : null;
      if (active && typeof active._gohaccpWedgeEnterHandler === "function") {
        swallowEnter(e);
        return;
      }
      if (scanCallback) {
        swallowEnter(e);
        completeBufferedScan({ defer: true });
      }
      return;
    }
    if (shouldBlockEnterNow()) {
      swallowEnter(e);
    }
    return;
  }

  if (!wedgeSessionActive || e.repeat) return;

  const ch = keyEventToChar(e);
  if (!ch || e.ctrlKey || e.metaKey || e.altKey) return;

  lastKeyAt = Date.now();
  blockEnterUntil = Date.now() + POST_SCAN_ENTER_MS;

  // Never build a barcode from keydown chars on Zebra — they are Sincoole
  // garbage ("000026sie"). Real data arrives via the focused input's `input` event.
  // Only re-focus hidden capture when that mode is active and focus was lost.
  if (wedgeUsesHiddenCapture && captureInput && document.activeElement !== captureInput) {
    focusSymbolWedgeCapture();
  }
}

function onWedgeKeyPress(e) {
  if ((isEnterKey(e) || isTabKey(e)) && (wedgeSessionActive || shouldBlockEnterNow())) {
    e.preventDefault();
    if (wedgeSessionActive && typeof fallbackScanGetter === "function") {
      const active = typeof document !== "undefined" ? document.activeElement : null;
      if (active && typeof active._gohaccpWedgeEnterHandler === "function") {
        return;
      }
    }
    if (wedgeSessionActive && scanCallback) {
      completeBufferedScan({ defer: true });
    }
  }
}

function onWedgeKeyUp(e) {
  if ((isEnterKey(e) || isTabKey(e)) && shouldBlockEnterNow()) {
    swallowEnter(e);
  }
}

function onShieldBeforeInput(e) {
  if (!shouldBlockEnterNow()) return;
  if (e.inputType === "insertLineBreak" || e.data === "\n" || e.data === "\r") {
    e.preventDefault();
  }
}

function onShieldSubmit(e) {
  if (!shouldBlockEnterNow()) return;
  e.preventDefault();
  e.stopPropagation();
}

function attachWedgeListeners() {
  if (wedgeTeardown || typeof window === "undefined") return;
  window.addEventListener("keydown", onWedgeKeyDown, CAPTURE_OPTS);
  window.addEventListener("keypress", onWedgeKeyPress, CAPTURE_OPTS);
  window.addEventListener("keyup", onWedgeKeyUp, CAPTURE_OPTS);
  window.addEventListener("beforeinput", onShieldBeforeInput, CAPTURE_OPTS);
  window.addEventListener("submit", onShieldSubmit, CAPTURE_OPTS);
  wedgeTeardown = () => {
    window.removeEventListener("keydown", onWedgeKeyDown, CAPTURE_OPTS);
    window.removeEventListener("keypress", onWedgeKeyPress, CAPTURE_OPTS);
    window.removeEventListener("keyup", onWedgeKeyUp, CAPTURE_OPTS);
    window.removeEventListener("beforeinput", onShieldBeforeInput, CAPTURE_OPTS);
    window.removeEventListener("submit", onShieldSubmit, CAPTURE_OPTS);
  };
}

/**
 * Capture wedge keystrokes at window level (works even when the input lags).
 * ownerId — unique per table row; re-focuses without tearing down when unchanged.
 */
export function startSymbolWedgeSession(
  onScan,
  onProgress,
  ownerId,
  getFallbackValue,
  { useHiddenCapture = false } = {}
) {
  const preservedEnterShield = blockEnterUntil;

  if (
    wedgeSessionActive &&
    ownerId &&
    activeWedgeOwnerId === ownerId &&
    wedgeTeardown
  ) {
    scanCallback = onScan;
    progressCallback = onProgress || null;
    fallbackScanGetter = getFallbackValue || null;
    wedgeUsesHiddenCapture = !!useHiddenCapture;
    blockEnterUntil = Math.max(preservedEnterShield, Date.now() + POST_SCAN_ENTER_MS);
    if (useHiddenCapture) {
      ensureCaptureInput();
      if (captureInput) captureInput.style.pointerEvents = "auto";
      focusSymbolWedgeCapture();
    } else if (captureInput) {
      captureInput.style.pointerEvents = "none";
    }
    return;
  }

  stopSymbolWedgeSession({ immediate: true, preserveEnterShield: true });
  activeWedgeOwnerId = ownerId || null;
  fallbackScanGetter = getFallbackValue || null;
  wedgeUsesHiddenCapture = !!useHiddenCapture;
  wedgeSessionActive = true;
  enterShieldActive = true;
  scanCallback = onScan;
  progressCallback = onProgress || null;
  keyBuffer = "";
  lastKeyAt = 0;
  blockEnterUntil = Math.max(preservedEnterShield, Date.now() + POST_SCAN_ENTER_MS);
  attachWedgeListeners();
  if (useHiddenCapture) {
    ensureCaptureInput();
    if (captureInput) captureInput.style.pointerEvents = "auto";
    clearCaptureInputValue();
    focusSymbolWedgeCapture();
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => focusSymbolWedgeCapture());
    }
  } else {
    if (captureInput) {
      captureInput.style.pointerEvents = "none";
    }
    if (captureInput && typeof document !== "undefined" && document.activeElement === captureInput) {
      // Visible-field mode: never leave focus on a leftover bottom capture input
      // (that scrolls the form away from the location field).
      try {
        captureInput.blur();
      } catch (_) {}
    }
  }
}

/** @deprecated use startSymbolWedgeSession */
export function installSymbolScanEnterShield() {
  startSymbolWedgeSession(() => {});
}

export function extendSymbolScanEnterShield(ms = POST_SCAN_ENTER_MS) {
  blockEnterUntil = Date.now() + ms;
}

/** Prime Enter shield before focus/wedge session (prevents Chrome home on first scan). */
export function primeWedgeEnterShield(ms = 15000) {
  blockEnterUntil = Math.max(blockEnterUntil, Date.now() + ms);
}

function teardownWedgeListeners({ preserveEnterShield = false } = {}) {
  if (wedgeTeardown) {
    wedgeTeardown();
    wedgeTeardown = null;
  }
  if (!preserveEnterShield) {
    blockEnterUntil = 0;
  }
}

export function stopSymbolWedgeSessionForOwner(ownerId, { immediate = false } = {}) {
  if (ownerId && activeWedgeOwnerId && ownerId !== activeWedgeOwnerId) {
    return;
  }
  stopSymbolWedgeSession({ immediate });
}

export function stopSymbolWedgeSession({ immediate = false, preserveEnterShield = false } = {}) {
  wedgeSessionActive = false;
  enterShieldActive = false;
  wedgeUsesHiddenCapture = false;
  activeWedgeOwnerId = null;
  fallbackScanGetter = null;
  scanCallback = null;
  progressCallback = null;
  keyBuffer = "";
  clearIdleFlush();
  clearCaptureInputValue();

  if (immediate) {
    teardownWedgeListeners({ preserveEnterShield });
    if (captureInput && document.activeElement === captureInput) {
      captureInput.blur();
    }
    return;
  }

  const finish = () => {
    if (Date.now() < blockEnterUntil) {
      setTimeout(finish, blockEnterUntil - Date.now() + 50);
      return;
    }
    teardownWedgeListeners();
  };
  finish();
}

/** @deprecated use stopSymbolWedgeSession */
export function uninstallSymbolScanEnterShield() {
  stopSymbolWedgeSession();
}

/* ---- Global scanner guard: works for ANY focused text input on mobile web ----
 *
 * Monitors DOM `input` event timing.  When ≥3 events arrive within 50 ms of
 * each other (scanner burst speed) on a text input:
 *   1. Sets a 2-second Enter/Tab block so the scanner's trailing newline does
 *      not navigate Chrome or submit the form.
 *   2. Schedules an 800 ms idle callback that normalises the focused field's
 *      value (strips Sincoole prefix garbage like "\000026").
 *
 * If a dedicated wedge session is active (location field) that session handles
 * its own Enter blocking and normalisation — the guard skips those fields.
 */

let globalScanGuardCount = 0;
let globalScanGuardTeardown = null;
let scanGuardLastInputAt = 0;
let scanGuardInputCount = 0;
let scanGuardBlockEnterUntil = 0;
let scanGuardIdleTimer = null;
let scanGuardLastValueLen = 0;

const SCAN_GUARD_GAP_MS = 50;       // input events < 50 ms apart → scanner burst
const SCAN_GUARD_MIN_INPUTS = 2;    // need ≥2 fast events before activating
const SCAN_GUARD_ENTER_BLOCK_MS = 2000;
const SCAN_GUARD_IDLE_MS = 800;

function applyScanGuardNormalization() {
  scanGuardIdleTimer = null;
  scanGuardInputCount = 0;
  if (wedgeSessionActive) return; // let the dedicated wedge session handle its field
  try {
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (!active) return;
    const tag = (active.tagName || "").toUpperCase();
    if (tag !== "INPUT" && tag !== "TEXTAREA") return;
    if (active.readOnly || active.disabled) return;
    const raw = active.value;
    if (!raw) return;
    const clean = normalizeWedgeScanText(raw);
    if (clean && clean !== raw) {
      // Mirror the React controlled-input trick used in syncDomScanValue:
      // reset the value tracker so React sees the change, then fire `input`.
      const tracker = active._valueTracker;
      if (tracker) tracker.setValue("");
      active.value = clean;
      active.dispatchEvent(new Event("input", { bubbles: true }));
    }
  } catch (_) {}
}

function onScanGuardInput(e) {
  if (!globalScanGuardCount) return;
  if (wedgeSessionActive) return;
  const target = e.target;
  if (!target) return;
  const tag = (target.tagName || "").toUpperCase();
  if (tag !== "INPUT" && tag !== "TEXTAREA") return;

  const now = Date.now();
  const gap = now - scanGuardLastInputAt;
  scanGuardLastInputAt = now;
  const valLen = (target.value || "").length;
  const charJump = valLen - scanGuardLastValueLen >= 3;
  scanGuardLastValueLen = valLen;

  if (charJump) {
    scanGuardInputCount = SCAN_GUARD_MIN_INPUTS;
  } else if (gap <= SCAN_GUARD_GAP_MS) {
    scanGuardInputCount++;
  } else {
    scanGuardInputCount = 1;
  }

  if (scanGuardInputCount >= SCAN_GUARD_MIN_INPUTS) {
    scanGuardBlockEnterUntil = now + SCAN_GUARD_ENTER_BLOCK_MS;
    if (scanGuardIdleTimer) clearTimeout(scanGuardIdleTimer);
    scanGuardIdleTimer = setTimeout(applyScanGuardNormalization, SCAN_GUARD_IDLE_MS);
  }
}

function onScanGuardKeydown(e) {
  if (!globalScanGuardCount) return;
  if (!isEnterKey(e) && !isTabKey(e)) return;
  if (Date.now() < scanGuardBlockEnterUntil) {
    swallowEnter(e);
    // Trigger idle normalization if not already scheduled
    if (!scanGuardIdleTimer) {
      scanGuardIdleTimer = setTimeout(applyScanGuardNormalization, SCAN_GUARD_IDLE_MS);
    }
  }
}

/**
 * Install a ref-counted global scanner guard that makes every text input on
 * the page work with a hardware barcode scanner (keyboard-wedge mode):
 *   – Enter/Tab after a scan burst is blocked (won't submit the form).
 *   – The scanned value is normalised (prefix garbage stripped) on idle.
 *
 * Call once per mounted form; balance with releaseGlobalScannerGuard().
 */
export function installGlobalScannerGuard() {
  globalScanGuardCount++;
  if (globalScanGuardTeardown || typeof window === "undefined") return;
  window.addEventListener("input", onScanGuardInput, CAPTURE_OPTS);
  window.addEventListener("keydown", onScanGuardKeydown, CAPTURE_OPTS);
  globalScanGuardTeardown = () => {
    window.removeEventListener("input", onScanGuardInput, CAPTURE_OPTS);
    window.removeEventListener("keydown", onScanGuardKeydown, CAPTURE_OPTS);
    if (scanGuardIdleTimer) { clearTimeout(scanGuardIdleTimer); scanGuardIdleTimer = null; }
    scanGuardInputCount = 0;
    scanGuardBlockEnterUntil = 0;
    scanGuardLastValueLen = 0;
  };
}

export function releaseGlobalScannerGuard() {
  globalScanGuardCount = Math.max(0, globalScanGuardCount - 1);
  if (globalScanGuardCount === 0 && globalScanGuardTeardown) {
    globalScanGuardTeardown();
    globalScanGuardTeardown = null;
  }
}
